import {
  Download,
  LayoutGrid,
  Layers,
  List as ListIcon,
  Search,
  Table as TableIcon,
} from 'lucide-react';
import { useMemo, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { SortableTableHead } from '@/components/ui/sortable-table-head';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ViewModeToggle, type ViewModeOption } from '@/components/ui/view-mode-toggle';
import { DateRangeFilter } from '@/components/ui/date-range-filter';
import { exportCsv, type ApiError } from '@/core/api';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { formatMoney } from '@/lib/money';
import { EXPENSE_BASE } from '../api';
import { EXPENSE_CATEGORIES, expenseCategoryLabel } from '../constants';
import { useExpenses } from '../queries';
import { useExpenseUi, type LedgerViewMode } from '../store/viewPrefs';
import type { ExpenseEntry } from '../types';

/** Columns the backend lets us order by — keeps typos out of the URL. */
type ExpenseOrderingColumn = 'date' | 'description' | 'category' | 'base_amount';

const VIEW_MODES: readonly ViewModeOption<LedgerViewMode>[] = [
  { value: 'table', label: 'Table', icon: TableIcon },
  { value: 'list', label: 'List', icon: ListIcon },
  { value: 'cards', label: 'Cards', icon: LayoutGrid },
  { value: 'grouped', label: 'Grouped by category', icon: Layers },
];

function formatDate(value: string | null): string {
  return value ? new Date(value).toLocaleDateString() : '—';
}

export function ExpenseListPage() {
  const view = useExpenseUi((s) => s.view);
  const setView = useExpenseUi((s) => s.setView);
  const ordering = useExpenseUi((s) => s.ordering);
  const setOrdering = useExpenseUi((s) => s.setOrdering);

  const [searchInput, setSearchInput] = useState('');
  // Live, debounced search — fires 300ms after typing stops (no Search button).
  const search = useDebouncedValue(searchInput.trim(), 300);
  const [recurring, setRecurring] = useState('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);

  const recurringParam = recurring === 'all' ? undefined : recurring === 'recurring';
  const { data, isPending, isError, error, isFetching } = useExpenses({
    page,
    search: search || undefined,
    recurring: recurringParam,
    after: from || undefined,
    before: to || undefined,
    ordering,
  });

  const results = data?.results ?? [];
  // Per-currency totals for the rows on this page — kept apart (no FX), so each
  // currency reads as its own subtotal rather than a converted lump sum.
  const pageTotals = results.reduce((acc, e) => {
    const code = e.currency ?? 'USD';
    acc.set(code, (acc.get(code) ?? 0) + Number(e.amount ?? 0));
    return acc;
  }, new Map<string, number>());
  const pageTotalLabel = Array.from(pageTotals.entries())
    .map(([code, amt]) => formatMoney(String(amt), code, { narrowSymbol: true }))
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Expenses</h1>
        <p className="text-muted-foreground text-sm">
          Read-only ledger — incl. SaaS subscriptions and contractor costs (USD base rollup).
        </p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-64">
            <Search className="text-muted-foreground absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
            <Input
              value={searchInput}
              onChange={(e) => {
                setPage(1);
                setSearchInput(e.target.value);
              }}
              placeholder="Search by description or vendor…"
              className="pl-8"
              aria-label="Search expenses"
            />
          </div>
          <Select
            value={recurring}
            onValueChange={(v) => {
              setPage(1);
              setRecurring(v);
            }}
          >
            <SelectTrigger className="h-9" aria-label="Recurring filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All expenses</SelectItem>
              <SelectItem value="recurring">Recurring only</SelectItem>
              <SelectItem value="oneoff">One-off only</SelectItem>
            </SelectContent>
          </Select>
          <DateRangeFilter
            label="Date"
            idPrefix="expense-date"
            from={from}
            to={to}
            onFromChange={(v) => { setPage(1); setFrom(v); }}
            onToChange={(v) => { setPage(1); setTo(v); }}
          />
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="icon"
            variant="outline"
            onClick={() => {
              // Mirror the live list filters and add `export=csv`; the backend
              // returns `Content-Disposition: attachment`, so this navigation
              // downloads `expenses.csv` without leaving the SPA.
              void exportCsv(EXPENSE_BASE, {
                search: search || undefined,
                recurring: recurringParam,
                after: from || undefined,
                before: to || undefined,
                ordering,
                export: 'csv',
              });
            }}
            aria-label="Export expenses as CSV"
            title="Export expenses as CSV"
          >
            <Download className="size-4" />
          </Button>
          <ViewModeToggle value={view} onChange={setView} modes={VIEW_MODES} />
        </div>
      </div>

      {isError ? (
        <p className="text-destructive text-sm">{(error as unknown as ApiError).message}</p>
      ) : isPending ? (
        <ExpenseSkeleton />
      ) : view === 'table' ? (
        <ExpenseTable entries={results} ordering={ordering} onOrderingChange={setOrdering} />
      ) : view === 'list' ? (
        <ExpenseList entries={results} />
      ) : view === 'cards' ? (
        <ExpenseCards entries={results} />
      ) : (
        <ExpenseGroupedByCategory entries={results} />
      )}

      {data && data.count > 0 ? (
        <div className="flex items-center justify-between">
          <p className="text-muted-foreground text-sm">
            {data.count} {data.count === 1 ? 'entry' : 'entries'} ·{' '}
            <span className="text-foreground font-medium tabular-nums">
              Page total {pageTotalLabel}
            </span>
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!data.previous || isFetching}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <span className="text-sm">Page {page}</span>
            <Button
              variant="outline"
              size="sm"
              disabled={!data.next || isFetching}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ---- View renderers --------------------------------------------------------

interface ExpenseViewProps {
  entries: ExpenseEntry[];
}

interface ExpenseTableProps extends ExpenseViewProps {
  ordering: string | undefined;
  onOrderingChange: (next: string | undefined) => void;
}

function ExpenseTable({ entries, ordering, onOrderingChange }: ExpenseTableProps) {
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <SortableTableHead<ExpenseOrderingColumn>
              column="date"
              value={ordering}
              onChange={onOrderingChange}
            >
              Date
            </SortableTableHead>
            <SortableTableHead<ExpenseOrderingColumn>
              column="description"
              value={ordering}
              onChange={onOrderingChange}
            >
              Description
            </SortableTableHead>
            <SortableTableHead<ExpenseOrderingColumn>
              column="category"
              value={ordering}
              onChange={onOrderingChange}
              className="hidden sm:table-cell"
            >
              Category
            </SortableTableHead>
            <TableHead className="hidden lg:table-cell">Vendor</TableHead>
            <TableHead className="hidden lg:table-cell">Employee</TableHead>
            <TableHead className="text-right">Amount</TableHead>
            <SortableTableHead<ExpenseOrderingColumn>
              column="base_amount"
              value={ordering}
              onChange={onOrderingChange}
              className="hidden text-right md:table-cell"
            >
              Base (USD)
            </SortableTableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {entries.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="text-muted-foreground py-10 text-center">
                No expense entries.
              </TableCell>
            </TableRow>
          ) : (
            entries.map((e) => (
              <TableRow key={e.id}>
                <TableCell>{formatDate(e.date)}</TableCell>
                <TableCell className="font-medium">
                  {e.description}
                  {e.is_recurring ? (
                    <Badge variant="outline" className="ml-2 text-[10px]">
                      Recurring
                    </Badge>
                  ) : null}
                </TableCell>
                <TableCell className="text-muted-foreground hidden sm:table-cell">
                  {expenseCategoryLabel(e.category)}
                </TableCell>
                <TableCell className="text-muted-foreground hidden lg:table-cell">
                  {e.vendor || '—'}
                </TableCell>
                <TableCell className="text-muted-foreground hidden lg:table-cell">
                  {e.employee_name || '—'}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatMoney(e.amount, e.currency) ?? '—'}
                </TableCell>
                <TableCell className="hidden text-right tabular-nums md:table-cell">
                  {formatMoney(e.base_amount, 'USD') ?? '—'}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function ExpenseCards({ entries }: ExpenseViewProps) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {entries.map((e) => (
        <Card key={e.id}>
          <CardHeader>
            <CardTitle className="text-base">{e.description}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap items-center gap-2 text-xs">
            <Badge variant="outline">{expenseCategoryLabel(e.category)}</Badge>
            {e.vendor ? <span className="text-muted-foreground truncate">{e.vendor}</span> : null}
            <span className="text-muted-foreground">{formatDate(e.date)}</span>
            <span className="ml-auto font-medium tabular-nums">
              {formatMoney(e.amount, e.currency) ?? '—'}
            </span>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function ExpenseList({ entries }: ExpenseViewProps) {
  return (
    <ul className="divide-y rounded-md border">
      {entries.map((e) => (
        <li key={e.id} className="flex items-center gap-3 px-3 py-2 text-sm">
          <span className="min-w-0 flex-1 truncate font-semibold">{e.description}</span>
          <span className="text-muted-foreground hidden truncate text-xs lg:inline">
            {e.vendor || '—'}
          </span>
          <Badge variant="outline" className="hidden shrink-0 sm:inline-flex">
            {expenseCategoryLabel(e.category)}
          </Badge>
          <span className="text-muted-foreground text-xs">{formatDate(e.date)}</span>
          <span className="font-medium tabular-nums">
            {formatMoney(e.amount, e.currency) ?? '—'}
          </span>
        </li>
      ))}
    </ul>
  );
}

function ExpenseGroupedByCategory({ entries }: ExpenseViewProps) {
  // Bucket by category, preserving the declared category order; skip empty groups.
  const groups = useMemo(() => {
    return EXPENSE_CATEGORIES.map((c) => ({
      key: c.value,
      label: c.label,
      bucket: entries.filter((e) => e.category === c.value),
    }));
  }, [entries]);

  return (
    <div className="space-y-6">
      {groups.map(({ key, label, bucket }) => {
        if (bucket.length === 0) return null;
        return (
          <section key={key} aria-labelledby={`expense-category-${key}`}>
            <h2
              id={`expense-category-${key}`}
              className="text-muted-foreground mb-2 flex items-center gap-2 text-xs font-semibold tracking-wider uppercase"
            >
              {label}
              <Badge variant="outline" className="text-[10px]">
                {bucket.length}
              </Badge>
            </h2>
            <ExpenseCards entries={bucket} />
          </section>
        );
      })}
    </div>
  );
}

function ExpenseSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-12 w-full" />
      ))}
    </div>
  );
}
